import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Crown, Save, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../lib/api-client';

interface ProfileBoosterSettingsData {
  adsEnabled: boolean;
  adFrequency: number; // Show ad after every N profiles
  bannerEnabled: boolean;
  analyticsEnabled: boolean;
  boostMultiplier: number; // Default boost multiplier
  minCreditsForBoost: number; // Minimum credits needed for boost
}

export function ProfileBoosterSettings() {
  const [settings, setSettings] = useState<ProfileBoosterSettingsData>({
    adsEnabled: true,
    adFrequency: 3,
    bannerEnabled: true,
    analyticsEnabled: true,
    boostMultiplier: 10,
    minCreditsForBoost: 1
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getProfileBoosterSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      // Silently use defaults if API not ready - no warning needed in demo mode
      console.log('Using default settings (API not available)');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiClient.updateProfileBoosterSettings(settings);
      if (response.success) {
        toast.success('Settings saved successfully');
        // Refetch to ensure we have the latest saved data
        await fetchSettings();
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin">
          <Crown className="size-8 text-pink-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Profile Booster Settings</h2>
        <p className="text-gray-600">Configure profile boost features and advertisements</p>
      </div>

      {/* Ad Display Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg">
            <TrendingUp className="size-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Advertisement Display</h3>
            <p className="text-sm text-gray-600">Control where and how often ads appear</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Enable Profile Booster Ads */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <Label htmlFor="ads-enabled" className="font-semibold text-base">
                Profile Booster Ads
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                Show profile booster ads throughout the website
              </p>
            </div>
            <Switch
              id="ads-enabled"
              checked={settings.adsEnabled}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, adsEnabled: checked })
              }
            />
          </div>

          {/* Ad Frequency */}
          {settings.adsEnabled && (
            <div>
              <Label htmlFor="ad-frequency" className="font-semibold">
                Ad Frequency
              </Label>
              <p className="text-sm text-gray-600 mb-2">
                Show one ad after every N profiles in listings
              </p>
              <div className="flex items-center gap-3">
                <Input
                  id="ad-frequency"
                  type="number"
                  min="1"
                  max="10"
                  value={settings.adFrequency}
                  onChange={(e) =>
                    setSettings({ ...settings, adFrequency: parseInt(e.target.value) || 3 })
                  }
                  className="w-24"
                />
                <span className="text-sm text-gray-600">
                  profiles (Recommended: 3-5)
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Example: If set to 3, an ad will appear after every 3 profiles
              </p>
            </div>
          )}

          {/* Banner Ads */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <Label htmlFor="banner-enabled" className="font-semibold text-base">
                Top Banner Ads
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                Show profile booster banner at the top of pages
              </p>
            </div>
            <Switch
              id="banner-enabled"
              checked={settings.bannerEnabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, bannerEnabled: checked })
              }
            />
          </div>
        </div>
      </Card>

      {/* Boost Configuration */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
            <Crown className="size-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Boost Configuration</h3>
            <p className="text-sm text-gray-600">Configure how profile boost works</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Boost Multiplier */}
          <div>
            <Label htmlFor="boost-multiplier" className="font-semibold">
              Boost Multiplier
            </Label>
            <p className="text-sm text-gray-600 mb-2">
              How many times more visible boosted profiles should be
            </p>
            <div className="flex items-center gap-3">
              <Input
                id="boost-multiplier"
                type="number"
                min="1"
                max="50"
                value={settings.boostMultiplier}
                onChange={(e) =>
                  setSettings({ ...settings, boostMultiplier: parseInt(e.target.value) || 10 })
                }
                className="w-24"
              />
              <span className="text-sm text-gray-600">x more visible</span>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Current Setting:</strong> Boosted profiles will appear{' '}
                <strong className="text-blue-700">{settings.boostMultiplier}x</strong> more often
                in search results and matches
              </p>
            </div>
          </div>

          {/* Minimum Credits */}
          <div>
            <Label htmlFor="min-credits" className="font-semibold">
              Minimum Credits for Boost
            </Label>
            <p className="text-sm text-gray-600 mb-2">
              Minimum credits needed to keep boost active
            </p>
            <div className="flex items-center gap-3">
              <Input
                id="min-credits"
                type="number"
                min="0"
                value={settings.minCreditsForBoost}
                onChange={(e) =>
                  setSettings({ ...settings, minCreditsForBoost: parseInt(e.target.value) || 1 })
                }
                className="w-24"
              />
              <span className="text-sm text-gray-600">credits</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Boost will automatically deactivate when credits fall below this number
            </p>
          </div>
        </div>
      </Card>

      {/* Analytics Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
            <Eye className="size-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Analytics & Tracking</h3>
            <p className="text-sm text-gray-600">Configure analytics features for boosted users</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Enable Analytics */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <Label htmlFor="analytics-enabled" className="font-semibold text-base">
                Profile View Analytics
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                Show detailed analytics to users with active boost
              </p>
            </div>
            <Switch
              id="analytics-enabled"
              checked={settings.analyticsEnabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, analyticsEnabled: checked })
              }
            />
          </div>

          {settings.analyticsEnabled && (
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Analytics Features Enabled:</h4>
              <ul className="space-y-1 text-sm text-green-800">
                <li>✓ Profile view count and history</li>
                <li>✓ Who viewed their profile</li>
                <li>✓ Daily/weekly/monthly view graphs</li>
                <li>✓ Profile performance insights</li>
                <li>✓ Boost effectiveness tracking</li>
              </ul>
            </div>
          )}
        </div>
      </Card>

      {/* Preview */}
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50">
        <h3 className="font-bold text-lg mb-4">Settings Preview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-1">Ad Display</p>
            <p className="text-xs text-gray-600">
              {settings.adsEnabled 
                ? `Showing ads every ${settings.adFrequency} profiles`
                : 'Ads disabled'}
            </p>
            <p className="text-xs text-gray-600">
              {settings.bannerEnabled ? 'Banner ads enabled' : 'Banner ads disabled'}
            </p>
          </div>
          <div className="p-4 bg-white rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-1">Boost Effect</p>
            <p className="text-xs text-gray-600">
              {settings.boostMultiplier}x visibility multiplier
            </p>
            <p className="text-xs text-gray-600">
              Minimum {settings.minCreditsForBoost} credits required
            </p>
          </div>
          <div className="p-4 bg-white rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-1">Analytics</p>
            <p className="text-xs text-gray-600">
              {settings.analyticsEnabled 
                ? 'Full analytics enabled for boosted users'
                : 'Analytics disabled'}
            </p>
          </div>
          <div className="p-4 bg-white rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-1">User Experience</p>
            <p className="text-xs text-gray-600">
              {settings.adsEnabled && settings.analyticsEnabled
                ? 'Optimal settings for revenue and engagement'
                : 'Some features disabled'}
            </p>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-pink-600 to-purple-600 text-white"
          size="lg"
        >
          {saving ? (
            <>
              <div className="animate-spin mr-2">
                <Crown className="size-5" />
              </div>
              Saving...
            </>
          ) : (
            <>
              <Save className="size-5 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}